var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { v4 as uuid } from 'uuid';
import styles from './Calendar.module.scss';
import { CalendarDate } from './components';
import { WEEK_DAYS } from '../../../../constants';
import { addDays, getDaysInMonth, getDay, startOfDay, startOfMonth, endOfMonth, subDays, subMonths, setDate, isBefore, isAfter, isSameDay, isWithinInterval, } from 'date-fns';
var Calendar = function (_a) {
    var focusDate = _a.focusDate, startDate = _a.startDate, endDate = _a.endDate, startCompareDate = _a.startCompareDate, endCompareDate = _a.endCompareDate, minDate = _a.minDate, maxDate = _a.maxDate, order = _a.order, compareOn = _a.compareOn, onChange = _a.onChange;
    var checkIsDisabled = function (date, min, max) {
        var start = min || date;
        var end = max || date;
        return isAfter(start, end) ? true : !isWithinInterval(date, { start: start, end: end });
    };
    var getMonthDates = function (length, current) {
        return Array.from({ length: length }).map(function (c, i) { return ({
            value: i + 1,
            current: current,
        }); });
    };
    var getCurrMonthDates = function () { return getMonthDates(getDaysInMonth(focusDate), true); };
    var getPrevMonthDates = function () {
        var prevDatesLength = getDay(startOfMonth(focusDate));
        var prevMonthDays = getMonthDates(getDaysInMonth(subMonths(focusDate, 1)), false);
        prevMonthDays.splice(0, prevMonthDays.length - prevDatesLength);
        return prevMonthDays;
    };
    var getNextMonthDates = function () {
        var nextDatesLength = 6 - getDay(endOfMonth(focusDate));
        return getMonthDates(nextDatesLength, false);
    };
    var getDates = function () { return getPrevMonthDates().concat(getCurrMonthDates(), getNextMonthDates()); };
    var getBeforeAfter = function (date, compare) {
        var currDate = setDate(focusDate, date.value);
        var afterStart = isAfter(currDate, subDays(compare ? startCompareDate : startDate, 1));
        var beforeEnd = isBefore(currDate, addDays(startOfDay(compare ? endCompareDate : endDate), 1));
        return { afterStart: afterStart, beforeEnd: beforeEnd };
    };
    var getActiveDates = function (compare) {
        return getCurrMonthDates().reduce(function (curr, date) {
            var _a = getBeforeAfter(date, compare), afterStart = _a.afterStart, beforeEnd = _a.beforeEnd;
            return afterStart && beforeEnd ? __spreadArray(__spreadArray([], curr, true), [date.value], false) : curr;
        }, []);
    };
    var _b = useState(getDates()), dates = _b[0], setDates = _b[1];
    var _c = useState([]), activeDates = _c[0], setActiveDates = _c[1];
    var _d = useState([]), activeCompareDates = _d[0], setActiveCompareDates = _d[1];
    useEffect(function () {
        setDates(getDates());
    }, [focusDate]);
    useEffect(function () {
        setActiveDates(getActiveDates());
    }, [dates, startDate, endDate]);
    useEffect(function () {
        setActiveCompareDates(getActiveDates(true));
    }, [dates, startCompareDate, endCompareDate]);
    var onDateSelect = function (date) {
        var start = compareOn ? startCompareDate : startDate;
        var end = compareOn ? endCompareDate : endDate;
        var newDate = setDate(focusDate, date);
        var focus = subMonths(focusDate, order);
        if (!isSameDay(start, end))
            return onChange(focus, newDate, newDate, '');
        if (isBefore(start, newDate))
            return onChange(focus, start, newDate, '');
        onChange(focus, newDate, start, '');
    };
    var renderDays = useMemo(function () { return (_jsx("ul", __assign({ className: styles.Days }, { children: WEEK_DAYS.map(function (d) { return (_jsx("li", __assign({ className: styles.Day }, { children: d }), uuid())); }) }))); }, []);
    var renderDates = useMemo(function () { return (_jsx("ul", __assign({ className: styles.Dates }, { children: dates.map(function (d) { return (_jsx(CalendarDate, { isDisabled: checkIsDisabled(setDate(focusDate, d.value), minDate, maxDate), date: d, focusDate: focusDate, activeDates: activeDates, activeCompareDates: activeCompareDates, compareOn: compareOn, onSelect: onDateSelect }, uuid())); }) }))); }, [dates]);
    return (_jsxs("section", __assign({ className: styles.Calendar, "data-testid": "calendar-month-".concat(order + 1) }, { children: [renderDays, renderDates] })));
};
Calendar.propTypes = {
    focusDate: PropTypes.instanceOf(Date).isRequired,
    startDate: PropTypes.instanceOf(Date).isRequired,
    endDate: PropTypes.instanceOf(Date).isRequired,
    startCompareDate: PropTypes.instanceOf(Date).isRequired,
    endCompareDate: PropTypes.instanceOf(Date).isRequired,
    order: PropTypes.number.isRequired,
    compareOn: PropTypes.bool.isRequired,
    onChange: PropTypes.func.isRequired,
};
export { Calendar };
//# sourceMappingURL=Calendar.js.map